import { Link } from "react-router-dom";
import {
  dependeDaIA, descreverMissao, prazoDaMissao, progressoDe, recadoDoProgresso,
  situacaoDaMissao, tipoDaMissao,
  type ChallengeConfig, type ChallengeEvent, type EventState,
} from "@nhomi/core";
import type { Member } from "@/data/members";
import { IconChevron } from "@/ui/icons";

/* ============================================================
   O progresso das missões

   A missão existia e ninguém via: o dono configurava "Mês asiático", o
   bônus caía no placar semanas depois, e no meio disso não havia um
   lugar dizendo "faltam 2". Meta invisível não puxa ninguém — é o mesmo
   motivo de a sequência dizer "🔥 4 de 7" no topo do feed.

   O progresso vem da FICHA do membro (`members/{uid}.events`), que o
   servidor já escreve a cada prato. Não custa leitura nova: o feed já
   escuta as fichas por causa do placar.
   ============================================================ */

/** Barra para contagem e coleção, bolinhas para sequência (quebrar dói de ver). */
export function Progresso(
  { e, estado, hoje, grande }: {
    e: ChallengeEvent; estado: EventState | undefined; hoje: string; grande?: boolean;
  },
) {
  const p = progressoDe(e, estado, hoje);
  if (tipoDaMissao(e) === "sequencia" && p.need <= 10) {
    return (
      <div className={`missao-bolinhas ${grande ? "grande" : ""}`} aria-hidden="true">
        {Array.from({ length: p.need }, (_, i) => (
          <span key={i} className={i < p.feitos ? (p.fechou ? "on fechou" : "on") : ""} />
        ))}
      </div>
    );
  }
  const pct = Math.round((p.feitos / p.need) * 100);
  return (
    <div className={`missao-barra ${grande ? "grande" : ""} ${p.fechou ? "fechou" : ""}`}
      role="progressbar" aria-valuenow={p.feitos} aria-valuemin={0} aria-valuemax={p.need}>
      <span style={{ width: `${Math.max(pct, p.feitos ? 6 : 0)}%` }} />
    </div>
  );
}

/**
 * A faixa do feed: as missões valendo agora, com o meu progresso. Some
 * quando não há missão aberta — faixa vazia é ruído no lugar mais caro.
 */
export function FaixaDeMissoes(
  { cid, config, eu, hoje }: { cid: string; config: ChallengeConfig; eu?: Member; hoje: string },
) {
  const abertas = config.events.filter((e) => situacaoDaMissao(e, hoje) === "valendo");
  if (!abertas.length) return null;
  // Primeiro as que ainda não fechei: a que já fechou não pede nada.
  const ordem = [...abertas].sort((a, b) =>
    Number(!!eu?.events?.[a.id]?.day) - Number(!!eu?.events?.[b.id]?.day));
  const mostradas = ordem.slice(0, 2);

  return (
    <Link className="card missoes-faixa" to={`/c/${cid}/missoes`}>
      {mostradas.map((e) => {
        const estado = eu?.events?.[e.id];
        const p = progressoDe(e, estado, hoje);
        return (
          <div key={e.id} className="missoes-faixa-linha">
            <span className="missoes-faixa-emoji">{e.emoji || "🎯"}</span>
            <div className="missoes-faixa-corpo">
              <div className="missoes-faixa-topo">
                <span className="missoes-faixa-nome">{e.name}</span>
                <span className="missoes-faixa-num">{p.feitos}/{p.need}</span>
              </div>
              <Progresso e={e} estado={estado} hoje={hoje} />
              <div className="missoes-faixa-sub">
                {recadoDoProgresso(e, estado, hoje)} · {prazoDaMissao(e, hoje)}
              </div>
            </div>
          </div>
        );
      })}
      <div className="missoes-faixa-mais">
        {abertas.length > mostradas.length
          ? `Ver as ${abertas.length} missões`
          : "Ver missões e a galera"}
        <IconChevron size={16} />
      </div>
    </Link>
  );
}

/** O que a missão pede, com o aviso da IA quando cabe. */
export function PedidoDaMissao({ e, config }: { e: ChallengeEvent; config: ChallengeConfig }) {
  return (
    <>
      {descreverMissao(e, config)}
      {tipoDaMissao(e) !== "colecao" && dependeDaIA(e) && (
        <span className="missao-ia"> · conta prato com calorias estimadas</span>
      )}
    </>
  );
}
